/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { typeOf, isEmpty, isNone } from '@ember/utils';
import moment from 'moment-timezone';
import { cached, tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import AnalyticsService from 'cio/services/analytics';
import { Editor } from 'cio/services/editor';

import ComposerEditableEntity from 'cio/utils/composer/composer-editable-entity';
import { BatchUpdateTemplateType } from 'cio/utils/actions/batch-update';

export const FIELD_NAMES: Record<string, string> = {
  bcc: 'BCC',
  body: 'Body',
  image_url: 'Image URL',
  link: 'Deep Link or Web URL',
  custom_data: 'Custom Data',
  from: 'From',
  layout: 'Layout',
  sound: 'sound',
  limit: 'Limit',
  preheader_text: 'Preheader',
  reply_to: 'Reply To',
  subject: 'Subject',
  to: 'To',
  url: 'URL',
  url_params: 'URL Parameters',
  snippets: 'Snippets',
  responses: 'Responses',
};

export const TYPE_FIELD_NAMES: Record<string, Record<string, string>> = {
  push: {
    title: 'Title',
  },
  create_event: {
    name: 'Event name',
    recipient: 'Recipient',
  },
  in_app: {
    body_json: 'Message attribute',
  },
  [BatchUpdateTemplateType.CreateEvent]: {
    name: 'Event name',
  },
};

interface ReviewErrorsArgs {
  errors: Record<string, any[]>;
  editableEntity: ComposerEditableEntity;
  showNoErrors?: boolean;
  fieldNames?: Record<string, string>;
  fieldNamesOverride?: Record<string, string>;
  onReview?: () => unknown;
}

export default class ReviewErrors extends Component<ReviewErrorsArgs> {
  @service analytics!: AnalyticsService;

  @tracked showModal = false;
  @tracked lastErrorCheckTime: number | null = null;

  get fieldNames(): Record<string, string> {
    if (this.args.fieldNamesOverride != null) {
      return { ...this.args.fieldNamesOverride };
    }

    let fieldNames = {
      ...FIELD_NAMES,
    };

    let { template } = this.args.editableEntity;

    if (!isNone(template)) {
      let typeFieldNames = TYPE_FIELD_NAMES[template.templateType];
      if (!isNone(typeFieldNames)) {
        fieldNames = { ...fieldNames, ...typeFieldNames };
      }
    }

    if (this.args.fieldNames != null) {
      fieldNames = { ...fieldNames, ...this.args.fieldNames };
    }

    return fieldNames;
  }

  get hasErrors() {
    let { errors } = this.args;
    if (typeOf(errors) !== 'object') {
      return false;
    }

    return Object.keys(this.fieldNames).some((key) => !isEmpty(errors[key]));
  }

  @cached
  get fieldErrors() {
    if (!this.hasErrors) {
      return [];
    }

    let { errors } = this.args;

    let allFieldErrors: { field: string; label: string; errors: string[] }[] = [];

    let { fieldNames } = this;

    Object.keys(fieldNames).forEach((field) => {
      let fieldErrors = errors[field];

      if (fieldErrors == null || fieldErrors.length === 0) {
        return;
      }

      allFieldErrors.push({
        field,
        label: fieldNames[field],
        errors: fieldErrors,
      });
    });

    return allFieldErrors;
  }

  get hasHeaderErrors() {
    let { errors } = this.args;
    if (typeOf(errors) !== 'object' || typeOf(errors.headers) !== 'array') {
      return;
    }

    return errors.headers.some((h) => !isEmpty(h.name) || !isEmpty(h.value));
  }

  get errorCount() {
    let count = 0;
    let { errors } = this.args;

    if (typeOf(errors) !== 'object') {
      return 0;
    }

    Object.keys(this.fieldNames).forEach(function (key) {
      if (typeOf(errors[key]) !== 'array') {
        if (!isEmpty(errors[key])) {
          count += 1;
        }
      } else {
        count += errors[key].length;
      }
    });

    this.lastErrorCheckTime = moment().unix();
    return count;
  }

  get analyticsData() {
    return {
      ...this.args.editableEntity.analyticsData,
      editor: Editor.VISUAL,
      errors: this.args.errors,
    };
  }

  @action
  showModalAction() {
    this.showModal = true;
    this.args.onReview?.();

    this.analytics.track('message', 'errors_viewed', this.analyticsData);
  }

  @action
  didClickHowDoIFixThis() {
    this.analytics.track('message', 'errors_researched', this.analyticsData);
  }
}
